import { useState } from "react";
import { toast } from "sonner";
import { useNavigate, useOutletContext } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, CartesianGrid } from "recharts";
import { Building2, DollarSign, Users, Truck, Car, Route, CreditCard, Hourglass, AlertTriangle, Percent, Bell } from "lucide-react";
import { KpiCard, PageHeader, Loading } from "@/components/portal/widgets";
import { ChartPanel, tooltipStyle, ErrorNote, CompanyLink, StatusBadge } from "@/components/founder/ui";
import founderApi, { useFounder, money, fmtMonth, fmtD, errMsg } from "@/lib/founderApi";
import { WinbackDialog } from "@/components/founder/CompanyDialogs";

const PLAN_COLORS = { starter: "#60a5fa", growth: "#34d399", fleet: "#fbbf24", enterprise: "#c084fc" };

export default function FounderOverview() {
  const { demo } = useOutletContext();
  const nav = useNavigate();
  const { data: o, error } = useFounder(`/platform/overview?include_demo=${demo}`);
  const { data: companies } = useFounder(`/platform/companies?include_demo=${demo}`);
  const { data: alerts } = useFounder("/platform/alerts");
  if (error) return <ErrorNote error={error} />;
  if (!o) return <Loading label="Loading platform data…" />;
  const s = o.subscriptions || {};
  const plans = Object.entries(o.plan_distribution || {}).map(([k, v]) => ({ name: k, value: v }));
  const recent = [...(companies || [])].sort((a, b) => (a.created_at < b.created_at ? 1 : -1)).slice(0, 6);

  return (
    <div className="space-y-8" data-testid="founder-overview">
      <PageHeader title="Platform overview" subtitle={`Every company on RideStamp${demo ? ", including demo accounts" : ""}. Tap any number or chart to dig in.`} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard icon={Building2} label="Companies" value={o.companies} sub={`+${o.new_this_month} this month · ${o.new_last_month} last month`} to="/founder/companies" testid="kpi-companies" />
        <KpiCard icon={DollarSign} label="MRR" accent="emerald" value={money(o.mrr)} sub={`${money(o.revenue?.this_month)} collected this month`} to="/founder/revenue" testid="kpi-mrr" />
        <KpiCard icon={CreditCard} label="Paying" accent="emerald" value={s.paying || 0} sub={`${s.monthly || 0} monthly · ${s.annual || 0} annual`} to="/founder/companies?status=active" testid="kpi-paying" />
        <KpiCard icon={Hourglass} label="On trial" value={s.trial || 0} sub={`${s.trial_ended || 0} trials ended`} to="/founder/companies?status=trial" testid="kpi-trial" />
        <KpiCard icon={AlertTriangle} label="Past due" accent="red" value={s.past_due || 0} sub={`${o.revenue?.failed_30 || 0} failed payments (30d)`} to="/founder/companies?status=past_due" testid="kpi-pastdue" />
        <KpiCard icon={Percent} label="Trial → paid" accent="amber" value={o.conversion_rate == null ? "—" : `${Math.round(o.conversion_rate * (o.conversion_rate <= 1 ? 100 : 1))}%`} sub={`${s.canceled || 0} canceled`} to="/founder/growth" testid="kpi-conversion" />
        <KpiCard icon={Truck} label="Drivers" accent="slate" value={o.drivers} sub={`${o.users} users in total`} to="/founder/companies?sort=drivers" testid="kpi-drivers" />
        <KpiCard icon={Car} label="Vehicles" accent="slate" value={o.vehicles} sub={`${o.trips_30d} trips in 30 days`} to="/founder/companies?sort=vehicles" testid="kpi-vehicles" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <ChartPanel title="Earnings by month" hint="Tap a bar for that month's payments" testid="chart-revenue">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={o.revenue_monthly} onClick={(e) => e?.activeLabel && nav(`/founder/revenue?month=${e.activeLabel}`)} style={{ cursor: "pointer" }}>
              <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#34d399" stopOpacity={0.4} /><stop offset="100%" stopColor="#34d399" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis dataKey="month" tickFormatter={fmtMonth} stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `$${v}`} width={50} />
              <Tooltip {...tooltipStyle} labelFormatter={fmtMonth} formatter={(v) => [money(v), "Earned"]} />
              <Area dataKey="amount" stroke="#34d399" fill="url(#rev)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartPanel>
        <ChartPanel title="New sign-ups" hint="Tap a month to see who joined" testid="chart-signups">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={o.signups_monthly}>
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis dataKey="month" tickFormatter={fmtMonth} stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} width={30} />
              <Tooltip {...tooltipStyle} labelFormatter={fmtMonth} formatter={(v) => [v, "Companies"]} />
              <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => nav(`/founder/companies?month=${d.month || d.payload?.month}`)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
        <ChartPanel title="Plans" hint="Tap a slice to see those companies" testid="chart-plans">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={plans} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={3} cursor="pointer" onClick={(d) => nav(`/founder/companies?plan=${d.name || d.payload?.name}`)}>
                {plans.map((p) => <Cell key={p.name} fill={PLAN_COLORS[p.name] || "#94a3b8"} stroke="none" />)}
              </Pie>
              <Tooltip {...tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            {plans.map((p) => (
              <button key={p.name} onClick={() => nav(`/founder/companies?plan=${p.name}`)} data-testid={`plan-legend-${p.name}`} className="flex items-center gap-1.5 text-xs capitalize text-slate-300 hover:text-white">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: PLAN_COLORS[p.name] }} /> {p.name} · {p.value}
              </button>
            ))}
          </div>
        </ChartPanel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartPanel title="Latest sign-ups" testid="recent-signups" action={<button onClick={() => nav("/founder/companies")} className="text-xs font-semibold text-blue-300 hover:text-blue-200" data-testid="recent-signups-all">See all</button>}>
          <ul className="divide-y divide-slate-800">
            {recent.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <div className="min-w-0"><CompanyLink id={c.id} name={c.name} testid={`recent-company-${c.id}`} /><p className="truncate text-xs text-slate-500">{c.owner?.name} · {fmtD(c.created_at)}</p></div>
                <div className="flex items-center gap-2 text-xs text-slate-400"><span className="capitalize">{c.plan}</span><StatusBadge value={c.status} /></div>
              </li>
            ))}
            {companies && !recent.length && <li className="py-3 text-sm text-slate-500">No companies yet.</li>}
          </ul>
        </ChartPanel>
        <AlertsPanel alerts={alerts} />
      </div>
      <p className="flex items-center gap-1.5 text-xs text-slate-600"><Route className="h-3.5 w-3.5" /> {o.trips} trips handled all-time · <Users className="h-3.5 w-3.5" /> payments source: {o.payments_source}</p>
    </div>
  );
}

function AlertsPanel({ alerts }) {
  const churn = alerts?.churn || [];
  const follow = alerts?.followups || [];
  const [mail, setMail] = useState(null);
  const [sending, setSending] = useState(false);
  const digest = async () => {
    setSending(true);
    try { await founderApi.post("/platform/digest/send-now"); toast.success("This week's digest is on its way to your inbox."); }
    catch (e) { toast.error(errMsg(e, "Couldn't send the digest.")); } finally { setSending(false); }
  };
  const btn = "rounded border border-slate-700 px-2 py-0.5 text-[11px] font-semibold text-blue-300 hover:bg-slate-800";
  const row = (a, i, kind) => (
    <li key={`${kind}-${i}`} className="flex items-start justify-between gap-3 py-2.5 text-sm" data-testid={`alert-${kind}-${i}`}>
      <div className="min-w-0">
        <CompanyLink id={a.org_id || a.id} name={a.name || a.org_name || a.company} />
        <p className="text-xs text-slate-400">{a.reason || a.text || a.message || a.detail || ""}{a.follow_up ? ` · due ${fmtD(a.follow_up)}` : ""}</p>
        {kind === "churn" && (
          <div className="mt-1.5 flex gap-1.5">
            <button className={btn} onClick={() => setMail({ a, kind: "trial" })} data-testid={`alert-reminder-${i}`}>Send reminder</button>
            {(a.failed_payments > 0 || /payment|past.?due|overdue|card/i.test(`${a.reason || ""} ${a.status || ""} ${a.kind || ""}`)) && <button className={btn} onClick={() => setMail({ a, kind: "payment" })} data-testid={`alert-payment-${i}`}>Payment email</button>}
          </div>
        )}
      </div>
      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${kind === "churn" ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>{kind === "churn" ? "Churn risk" : "Follow up"}</span>
    </li>
  );
  return (
    <ChartPanel title="Needs your attention" testid="founder-alerts"
      action={<button onClick={digest} disabled={sending} className={btn} data-testid="alerts-digest-btn">{sending ? "Sending…" : "Send this week's digest now"}</button>}>
      {!churn.length && !follow.length ? (
        <p className="flex items-center gap-2 text-sm text-slate-500"><Bell className="h-4 w-4" /> Nothing needs you right now — no churn risks or follow-ups due.</p>
      ) : <ul className="divide-y divide-slate-800">{churn.map((a, i) => row(a, i, "churn"))}{follow.map((a, i) => row(a, i, "followup"))}</ul>}
      {mail && <WinbackDialog kind={mail.kind} company={{ id: mail.a.org_id || mail.a.id, name: mail.a.name || mail.a.org_name, owner: { name: mail.a.owner_name || mail.a.owner?.name } }} open onOpenChange={(v) => { if (!v) setMail(null); }} />}
    </ChartPanel>
  );
}
